// Shared SVG content for NoRecords and NoRecordsSmall icons.
// Both use the same viewBox and paths — only the rendered width/height differs.
import React from 'react';
import Svg, { Ellipse, Path, Rect } from 'react-native-svg';

interface NoRecordsBaseProps {
  width: string | number;
  height: string | number;
  /** Large background blob fill. Default: #F4F4F4 */
  bgColor?: string;
  /** Shadow / angled layer + small decorative dot. Default: #D9D9D9 */
  shadowColor?: string;
  /** Document body, tilted card, and search circle fill. Default: #FFFFFF */
  color?: string;
  /** Stroke / outline colour for all paths and decorative elements. Default: #AFAFAF */
  borderColor?: string;
  /** Magnifier / ellipse inner circle fill. Default: #F3F4F6 */
  circleColor?: string;
}

const NoRecordsBase: React.FC<NoRecordsBaseProps> = ({
  width,
  height,
  bgColor = '#F4F4F4',
  shadowColor = '#D9D9D9',
  color = '#FFFFFF',
  borderColor = '#AFAFAF',
  circleColor = '#F3F4F6',
}) => (
  <Svg width={width} height={height} viewBox="0 0 141 141" fill="none">
    {/* Large background blob */}
    <Path
      d="M39.0626 51.4042C58.2483 49.1494 64.4104 46.7703 77.71 36.4815C97.4586 21.208 126.92 21.1388 135.345 50.3544C141.823 72.8245 135.577 100.703 121.2 113.485C96.9915 135.01 52.1874 138.858 24.3768 122.252C0.400762 107.938 -7.30373 80.4005 7.64613 62.4541C12.7166 56.3672 20.6865 53.5632 39.0626 51.4042Z"
      fill={bgColor}
    />
    {/* Shadow / angled layer behind document */}
    <Path
      d="M30.868 51.2419C32.0688 46.9116 36.0106 43.9141 40.5044 43.9141H106.162C112.677 43.9141 117.452 50.0448 115.857 56.3619L101.15 114.618L21 86.8286L30.868 51.2419Z"
      fill={shadowColor}
    />
    {/* Tilted card / paper behind main document */}
    <Rect
      x="0.707989"
      y="1.22628"
      width="57.5201"
      height="59.8889"
      rx="9"
      transform="matrix(0.9656 0.260032 -0.257611 0.966249 49.226 18.7967)"
      fill={color}
      stroke={borderColor}
      strokeWidth="2"
    />
    {/* Inner circle / magnifier body */}
    <Ellipse
      cx="71.0506"
      cy="47.0795"
      rx="9.45"
      ry="9.49749"
      fill={circleColor}
    />
    {/* X mark inside circle — line 1 */}
    <Path
      d="M76.5432 44.4487L66.2554 50.4182"
      stroke={borderColor}
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* X mark inside circle — line 2 */}
    <Path
      d="M68.4294 42.2637L74.3691 52.6032"
      stroke={borderColor}
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Main document / folder shape */}
    <Path
      d="M93.3798 122.006C95.5334 122.006 97.5987 121.142 99.1215 119.606C100.644 118.069 101.5 115.985 101.5 113.812V72.8422C101.5 70.669 100.644 68.5849 99.1215 67.0482C97.5987 65.5116 95.5334 64.6483 93.3798 64.6483H61.3058C59.9478 64.6617 58.6081 64.3312 57.4094 63.6871C56.2107 63.0429 55.1913 62.1056 54.4444 60.961L51.1558 56.0447C50.4164 54.9118 49.4099 53.9818 48.2265 53.3382C47.0431 52.6947 45.7199 52.3576 44.3756 52.3574H28.4198C26.2662 52.3574 24.2009 53.2207 22.6781 54.7574C21.1553 56.294 20.2998 58.3782 20.2998 60.5513V113.812C20.2998 115.985 21.1553 118.069 22.6781 119.606C24.2009 121.142 26.2662 122.006 28.4198 122.006H93.3798Z"
      fill={color}
      stroke={borderColor}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Search handle — thin diagonal */}
    <Path
      d="M117.774 131.327L103.85 117.333"
      stroke={borderColor}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Search handle — thick diagonal */}
    <Path
      d="M120.05 133.614L110.774 124.292"
      stroke={borderColor}
      strokeWidth="8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Search circle */}
    <Path
      d="M85.8661 125.054C100.041 125.054 111.533 113.505 111.533 99.258C111.533 85.0115 100.041 73.4624 85.8661 73.4624C71.6908 73.4624 60.1995 85.0115 60.1995 99.258C60.1995 113.505 71.6908 125.054 85.8661 125.054Z"
      fill={color}
      stroke={borderColor}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Decorative + (top-right) — vertical */}
    <Path
      d="M136.149 1V8.73869"
      stroke={borderColor}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Decorative + (top-right) — horizontal */}
    <Path
      d="M132.299 4.86914L139.999 4.86914"
      stroke={borderColor}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Decorative + (top-left) — vertical */}
    <Path
      d="M22.7498 19.291V27.0297"
      stroke={borderColor}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Decorative + (top-left) — horizontal */}
    <Path
      d="M18.8999 23.1602H26.5999"
      stroke={borderColor}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Decorative + (bottom-left) — vertical */}
    <Path
      d="M10.8499 122.006V129.745"
      stroke={borderColor}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Decorative + (bottom-left) — horizontal */}
    <Path
      d="M7 125.875H14.7"
      stroke={borderColor}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Small decorative dot */}
    <Ellipse cx="67.5503" cy="6.9795" rx="1.75" ry="1.75879" fill={shadowColor} />
  </Svg>
);

export default NoRecordsBase;